import { useMemo, useEffect } from 'react';

import PropTypes from 'prop-types';

import { useCartContents } from '@zenbusiness/application-commons-ecommerce';
import useAsyncCallback from '@zenbusiness/application-commons-hooks/useAsyncCallback';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import { usePriceList } from '@zenbusiness/application-commons-pricelist';
import Stack from '@zenbusiness/zui/Stack';

import CalloutAsset from '#client/components/CalloutAsset';
import ExtraInfoCard from '#client/components/ExtraInfoCard';
import PageHeader from '#client/components/PageHeader';
import ProductOfferView from '#client/components/ProductOfferView';
import useFlowState from '#client/hooks/flow/useFlowState';
import useAddToCart from '#client/hooks/useAddToCart';
import useFlowNavigation from '#client/hooks/useFlowNavigation';
import useProductOfferViewProps from '#client/hooks/useProductOfferViewProps';
import useRemoveFromCart from '#client/hooks/useRemoveFromCart';
import TwoColumnLayout from '#client/layouts/TwoColumnLayout';
import { findProductInCart } from '#client/utils/product';

const ANNUAL_PRODUCT_NAME = 'ongoing_bof_compliance';
const ONETIME_PRODUCT_NAME = 'beneficial_ownership_filing';
const ANNUAL_OPTION = 'annual';
const ONETIME_OPTION = 'onetime';
const SERVICES = [ONETIME_PRODUCT_NAME, ANNUAL_PRODUCT_NAME];
const MESSAGE_KEY = 'shopping.page.beneficialOwnershipInformation';

export default function BeneficialOwnershipInformationPage({ slotProps }) {
  const { get: getProducts } = usePriceList();
  const products = getProducts(SERVICES);
  const {
    pricing: onetimePricing,
    pricing: { coupons }
  } = products.find((p) => p.pricing.interval === ONETIME_OPTION);
  const { pricing: annualPricing } = products.find((p) => p.pricing.interval === ANNUAL_OPTION);
  const { price: onetimePrice, servicePricingUuid: onetimeServicePricingUuid } =
    onetimePricing || {};
  const { price: annualPrice, servicePricingUuid: annualServicePricingUuid } = annualPricing || {};
  const { goToNextPage } = useFlowNavigation();
  const { addedToCart, addToCartBOI } = useAddToCart();
  const { removedFromCart, removeFromCart } = useRemoveFromCart();
  const { businessInfo } = useFlowState();
  const { mutating, items: cartItems } = useCartContents();
  const { jurisdictionState } = businessInfo;
  const { secondaryAction } = slotProps || {};

  // coupon logic, by default get first coupon returned
  // default amount to 0 keeps calculation of annual pricing consistent if a coupon does not exist.
  const { amount: couponAmount = 0, uuid: couponUuid = undefined } = coupons?.[0] ?? {};

  useEffect(() => {
    if (addedToCart || removedFromCart) {
      goToNextPage();
    }
  }, [addedToCart, goToNextPage, removedFromCart]);

  const handlePrimaryActionClick = useAsyncCallback(
    async (selectedOptionId) => {
      // add one time product to cart always
      // determine if coupon needs to be added based on annual selection
      const itemsToAdd = [
        {
          servicePricingUuid: onetimeServicePricingUuid,
          ...(selectedOptionId === ANNUAL_OPTION && couponUuid && { couponUuid }),
          jurisdictionState
        }
      ];
      if (selectedOptionId === ANNUAL_OPTION) {
        itemsToAdd.push({
          servicePricingUuid: annualServicePricingUuid,
          jurisdictionState
        });
      }
      await addToCartBOI(itemsToAdd);
    },
    [
      addToCartBOI,
      onetimeServicePricingUuid,
      annualServicePricingUuid,
      couponUuid,
      jurisdictionState
    ]
  );

  const handleSecondaryActionClick = useAsyncCallback(async () => {
    await removeFromCart([
      { servicePricingUuid: onetimeServicePricingUuid },
      { servicePricingUuid: annualServicePricingUuid }
    ]);
  }, [removeFromCart, onetimeServicePricingUuid, annualServicePricingUuid]);

  const purchaseOptions = useMemo(
    () => [
      {
        id: ANNUAL_OPTION,
        price: annualPrice + onetimePrice - couponAmount,
        description: true,
        priceNote: true,
        priceNotePrice: annualPrice,
        features: 3
      },
      {
        id: ONETIME_OPTION,
        price: onetimePrice,
        priceNote: true,
        description: true,
        features: 1
      }
    ],
    [annualPrice, couponAmount, onetimePrice]
  );

  const selectedOption = useMemo(() => {
    const annualProductInCart = findProductInCart(cartItems, ANNUAL_PRODUCT_NAME);
    const onetimeProductInCart = findProductInCart(cartItems, ONETIME_PRODUCT_NAME);

    if (onetimeProductInCart && !annualProductInCart) {
      return purchaseOptions[1];
    }

    return purchaseOptions[0];
  }, [cartItems, purchaseOptions]);

  const productOfferViewProps = useProductOfferViewProps({
    id: 'beneficialOwnershipInformation',
    chip: true,
    mutating,
    purchaseOptions,
    selectedOption,
    itemAssets: ['eventavailablerounded', 'addmoderatorrounded', 'requestquoterounded'],
    primaryAction: {
      'data-testid': 'beneficial-ownership-information-primary-action',
      onClick: handlePrimaryActionClick
    },
    secondaryAction: {
      'data-testid': 'beneficial-ownership-information-secondary-action',
      onClick: handleSecondaryActionClick,
      ...secondaryAction
    }
  });

  return (
    <TwoColumnLayout>
      <TwoColumnLayout.SmallColumn>
        <PageHeader
          subtitle={<FormattedMessage id={`${MESSAGE_KEY}.pageHeader.subtitle`} />}
          title={<FormattedMessage id={`${MESSAGE_KEY}.pageHeader.title`} />}
        />
        <ProductOfferView {...productOfferViewProps} />
      </TwoColumnLayout.SmallColumn>
      <TwoColumnLayout.LargeColumn>
        <Stack spacing={{ xs: 3, md: 4 }}>
          <ExtraInfoCard messageKey={MESSAGE_KEY} />
          <CalloutAsset name="funnel-bof" />
        </Stack>
      </TwoColumnLayout.LargeColumn>
    </TwoColumnLayout>
  );
}

BeneficialOwnershipInformationPage.propTypes = {
  slotProps: PropTypes.shape({
    secondaryAction: PropTypes.object
  })
};
